import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PolicyModal } from '../components/PolicyModal';
import { TextOfferSection } from '../components/TextOfferSection';
import { FloatingCTA } from '../components/FloatingCTA';
import { PageTracker } from '../components/PageTracker';
import { Share2, Printer, Bookmark, MessageSquare } from 'lucide-react';
type PolicyType = 'privacy' | 'terms' | 'refund';
export function AdvertorialPage() {
  const [policyModal, setPolicyModal] = useState<{
    isOpen: boolean;
    type: PolicyType;
  }>({
    isOpen: false,
    type: 'privacy'
  });
  const openPolicy = (type: PolicyType) => {
    setPolicyModal({
      isOpen: true,
      type
    });
  };
  const closePolicy = () => {
    setPolicyModal({
      ...policyModal,
      isOpen: false
    });
  };
  // Simple scroll progress
  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollTop;
      const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scroll = `${totalScroll / windowHeight}`;
      setScrollProgress(Number(scroll));
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen bg-[#FDFBF7] text-[#1A1A1A] font-serif selection:bg-stone-200" data-id="element-1402">
      <PageTracker data-id="element-1403" />

      {/* Top Disclaimer Bar */}
      <div className="bg-[#F5F5F5] border-b border-[#E5E5E5] py-1" data-id="element-1404">
        <div className="max-w-5xl mx-auto px-4 flex justify-center sm:justify-between items-center text-[10px] uppercase tracking-widest text-stone-500 font-sans" data-id="element-1405">
          <span data-id="element-1406">Advertisement</span>
          <span className="hidden sm:inline" data-id="element-1407">Sponsored Content</span>
        </div>
      </div>

      {/* Navigation / Masthead */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40" data-id="element-1408">
        <div className="h-1 bg-stone-900 w-full absolute top-0 left-0 transform origin-left transition-transform duration-100" style={{
        transform: `scaleX(${scrollProgress})`
      }} data-id="element-1409" />
        <div className="max-w-5xl mx-auto px-4 py-4 flex justify-between items-center" data-id="element-1410">
          <div className="font-serif text-xl sm:text-2xl font-bold tracking-tight text-stone-900" data-id="element-1411">
            HEALTH & WELLNESS
          </div>
          <div className="hidden sm:flex items-center gap-6 font-sans text-xs font-medium uppercase tracking-wider text-stone-500" data-id="element-1412">
            <span className="text-stone-900" data-id="element-1413">Science</span>
            <span className="hover:text-stone-900 cursor-pointer" data-id="element-1414">
              Lifestyle
            </span>
            <span className="hover:text-stone-900 cursor-pointer" data-id="element-1415">Aging</span>
            <span className="hover:text-stone-900 cursor-pointer" data-id="element-1416">
              Recovery
            </span>
          </div>
          <Link to="/home" className="sm:hidden font-sans text-xs font-bold uppercase border border-stone-900 px-3 py-1" data-id="element-1417">
            Menu
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 sm:py-12 grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-1418">
        {/* Main Article Column */}
        <article className="lg:col-span-8" data-id="element-1419">
          {/* Article Header */}
          <header className="mb-10 border-b border-stone-200 pb-8" data-id="element-1420">
            <div className="font-sans text-xs font-bold text-[#E87722] uppercase tracking-widest mb-4" data-id="element-1421">
              Pain Management
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-stone-900 leading-[1.1] mb-6" data-id="element-1422">
              Why Your Pain Keeps Coming Back (Even After Using "Strong" Creams)
            </h1>

            <h2 className="text-lg sm:text-xl text-stone-600 font-serif leading-relaxed mb-8" data-id="element-1423">
              If you've tried numbing creams and wondered why relief never
              lasts, the answer lies in how your body processes temperature—not
              medication.
            </h2>

            <div className="flex items-center justify-between font-sans text-xs text-stone-500 border-t border-stone-100 pt-6" data-id="element-1424">
              <div className="flex items-center gap-3" data-id="element-1425">
                <div className="w-8 h-8 bg-stone-200 rounded-full flex items-center justify-center text-stone-600 font-bold" data-id="element-1426">
                  S.J.
                </div>
                <div data-id="element-1427">
                  <div className="font-bold text-stone-900 uppercase tracking-wide" data-id="element-1428">
                    Sarah Jenkins
                  </div>
                  <div data-id="element-1429">Health Correspondent</div>
                </div>
              </div>
              <div className="text-right" data-id="element-1430">
                <div data-id="element-1431">Dec 22, 2025</div>
                <div data-id="element-1432">4 min read</div>
              </div>
            </div>
          </header>

          {/* Article Body */}
          <div className="prose prose-lg prose-stone max-w-none font-serif" data-id="element-1433">
            <p className="first-letter:text-5xl first-letter:font-bold first-letter:text-stone-900 first-letter:mr-3 first-letter:float-left leading-relaxed" data-id="element-1434">
              If you've ever rubbed on a pain cream and thought,{' '}
              <em data-id="element-1435">"Okay… that helped. But why does the pain always return?"</em>{' '}
              you are not alone. Millions of people cycle through numbing
              agents, ice packs, and menthol gels, only to find themselves in
              the same position hours later.
            </p>

            <p data-id="element-1436">
              The problem isn't that you're "getting old" or that your body is
              failing. The problem is that you've likely been using the wrong
              kind of relief.
            </p>

            <p data-id="element-1437">
              Most pain creams are designed to silence your nerves. They flood
              the area with menthol or numbing agents until your brain stops
              receiving the signal. The pain disappears, not because the issue
              is resolved, but because the connection has been severed. Once the
              numbing fades, the pain returns—often more intensely than before.
            </p>

            <div className="my-10 p-8 bg-[#F5F5F5] border-l-4 border-stone-900 italic text-xl text-stone-800" data-id="element-1438">
              "Most pain creams are designed to shut you up, not heal you. They
              don't help your body recover. They overwhelm it."
            </div>

            <h3 className="font-sans font-bold text-xl uppercase tracking-wide text-stone-900 mt-12 mb-6" data-id="element-1439">
              The Science of Suppression vs. Support
            </h3>

            <p data-id="element-1440">
              Muscles tighten for a reason. Joints ache for a reason.
              Inflammation is a biological response, not a random event. In most
              cases of chronic stiffness or soreness, the root cause is simple:{' '}
              <strong data-id="element-1441">
                restricted circulation combined with locked tissue.
              </strong>
            </p>

            <p data-id="element-1442">
              Your body is asking for blood flow and release. Instead, the
              standard approach is to apply ice or cooling menthol. This
              restricts blood flow further, tightening the tissue even more. It
              feels good in the moment because it shocks the nerves, but it
              works against the body's natural recovery process.
            </p>

            <h3 className="font-sans font-bold text-xl uppercase tracking-wide text-stone-900 mt-12 mb-6" data-id="element-1443">
              A Different Approach to Relief
            </h3>

            <p data-id="element-1444">
              This understanding has led to a shift in how experts view pain
              management. The focus is moving away from "numbing" and toward
              "restoring."
            </p>

            <p data-id="element-1445">
              One solution gaining attention is <strong data-id="element-1446">GINEXT</strong>, a
              botanical warming oil that operates on a completely different
              principle. Instead of masking pain signals, it uses high-potency
              ginger root and black pepper oil to generate deep, sustained
              warmth.
            </p>

            <p data-id="element-1447">
              Controlled, therapeutic warmth tells the nervous system three
              things:
            </p>

            <ul className="list-none pl-0 space-y-4 my-8 border-y border-stone-200 py-8" data-id="element-1448">
              <li className="flex gap-4" data-id="element-1449">
                <span className="font-bold text-stone-300 text-2xl" data-id="element-1450">01</span>
                <span data-id="element-1451">It is safe to loosen the muscle fibers.</span>
              </li>
              <li className="flex gap-4" data-id="element-1452">
                <span className="font-bold text-stone-300 text-2xl" data-id="element-1453">02</span>
                <span data-id="element-1454">It is okay to relax the guarding reflex.</span>
              </li>
              <li className="flex gap-4" data-id="element-1455">
                <span className="font-bold text-stone-300 text-2xl" data-id="element-1456">03</span>
                <span data-id="element-1457">Circulation can be restored to the area.</span>
              </li>
            </ul>

            <p data-id="element-1458">
              When circulation returns and tissue softens, relief follows
              naturally—without disconnecting your body from what it's feeling.
            </p>

            <h3 className="font-sans font-bold text-xl uppercase tracking-wide text-stone-900 mt-12 mb-6" data-id="element-1459">
              Why Ingredients Matter
            </h3>

            <p data-id="element-1460">
              Unlike synthetic gels that sit on the surface, botanical oils like
              those found in GINEXT are designed to absorb. The formulation
              includes:
            </p>

            <p data-id="element-1461">
              <strong data-id="element-1462">Ginger & Black Pepper:</strong> To drive natural heat and
              blood flow.
              <br data-id="element-1463" />
              <strong data-id="element-1464">Eucalyptus:</strong> To calm pain signals without numbing.
              <br data-id="element-1465" />
              <strong data-id="element-1466">Rosemary:</strong> To support long-term tissue health.
            </p>

            <p data-id="element-1467">
              For people who wake up stiff, carry tension in their neck, or deal
              with lower back tightness, this approach offers something menthol
              cannot: a return to normal function.
            </p>

            <div className="my-12" data-id="element-1468">
              <h4 className="font-sans font-bold text-sm uppercase tracking-widest text-stone-500 mb-4 text-center" data-id="element-1469">
                Reader Experience
              </h4>
              <p className="text-center font-serif text-lg italic text-stone-800" data-id="element-1470">
                "It doesn't feel like a medical treatment. It feels like my body
                is finally letting go of the tension it's been holding for
                years."
              </p>
            </div>

            <h3 className="font-sans font-bold text-xl uppercase tracking-wide text-stone-900 mt-12 mb-6" data-id="element-1471">
              The Verdict
            </h3>

            <p data-id="element-1472">
              If you are content with temporary relief that requires constant
              reapplication, traditional creams work fine. But if you are
              looking to support your body's ability to resolve tension, warmth
              may be the missing variable.
            </p>

            <p data-id="element-1473">
              GINEXT is currently available for direct purchase, allowing users
              to test the difference between numbing pain and resolving it.
            </p>

            {/* Offer Section */}
            <TextOfferSection data-id="element-1474" />
          </div>
        </article>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-12" data-id="element-1475">
          {/* About Section */}
          <div className="bg-stone-50 p-6 border-t-4 border-stone-900" data-id="element-1476">
            <h4 className="font-sans text-xs font-bold uppercase tracking-widest mb-4" data-id="element-1477">
              About This Series
            </h4>
            <p className="font-serif text-sm text-stone-600 leading-relaxed mb-4" data-id="element-1478">
              "The Recovery Report" investigates modern approaches to pain
              management, moving beyond pills and quick fixes to find
              sustainable solutions for longevity and mobility.
            </p>
          </div>

          {/* Trending */}
          <div data-id="element-1479">
            <h4 className="font-sans text-xs font-bold uppercase tracking-widest mb-6 border-b border-stone-200 pb-2" data-id="element-1480">
              Trending Now
            </h4>
            <ul className="space-y-6" data-id="element-1481">
              <li className="group cursor-pointer" data-id="element-1482">
                <div className="text-[#E87722] text-xs font-bold mb-1" data-id="element-1483">01</div>
                <div className="font-serif text-lg leading-tight group-hover:text-stone-600 transition-colors" data-id="element-1484">
                  The Hidden Cost of "Tech Neck" on Your Spine
                </div>
              </li>
              <li className="group cursor-pointer" data-id="element-1485">
                <div className="text-[#E87722] text-xs font-bold mb-1" data-id="element-1486">02</div>
                <div className="font-serif text-lg leading-tight group-hover:text-stone-600 transition-colors" data-id="element-1487">
                  Why Inflammation is Actually Your Friend (Sometimes)
                </div>
              </li>
              <li className="group cursor-pointer" data-id="element-1488">
                <div className="text-[#E87722] text-xs font-bold mb-1" data-id="element-1489">03</div>
                <div className="font-serif text-lg leading-tight group-hover:text-stone-600 transition-colors" data-id="element-1490">
                  Natural vs. Synthetic: The Absorption Debate
                </div>
              </li>
            </ul>
          </div>

          {/* Share Tools */}
          <div data-id="element-1491">
            <h4 className="font-sans text-xs font-bold uppercase tracking-widest mb-6 border-b border-stone-200 pb-2" data-id="element-1492">
              Share This Article
            </h4>
            <div className="flex gap-4" data-id="element-1493">
              <button className="w-10 h-10 border border-stone-200 flex items-center justify-center hover:bg-stone-50 transition-colors text-stone-600" data-id="element-1494">
                <Share2 className="w-4 h-4" data-id="element-1495" />
              </button>
              <button className="w-10 h-10 border border-stone-200 flex items-center justify-center hover:bg-stone-50 transition-colors text-stone-600" data-id="element-1496">
                <Printer className="w-4 h-4" data-id="element-1497" />
              </button>
              <button className="w-10 h-10 border border-stone-200 flex items-center justify-center hover:bg-stone-50 transition-colors text-stone-600" data-id="element-1498">
                <Bookmark className="w-4 h-4" data-id="element-1499" />
              </button>
              <button className="w-10 h-10 border border-stone-200 flex items-center justify-center hover:bg-stone-50 transition-colors text-stone-600" data-id="element-1500">
                <MessageSquare className="w-4 h-4" data-id="element-1501" />
              </button>
            </div>
          </div>
        </aside>
      </main>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-stone-400 py-12 mt-12" data-id="element-1502">
        <div className="max-w-5xl mx-auto px-4" data-id="element-1503">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 text-sm" data-id="element-1504">
            <div data-id="element-1505">
              <h5 className="text-white font-sans font-bold uppercase tracking-widest mb-4" data-id="element-1506">
                Health & Wellness
              </h5>
              <p className="font-serif leading-relaxed" data-id="element-1507">
                Dedicated to providing evidence-based information on natural
                recovery and pain management strategies.
              </p>
            </div>
            <div data-id="element-1508">
              <h5 className="text-white font-sans font-bold uppercase tracking-widest mb-4" data-id="element-1509">
                Legal
              </h5>
              <ul className="space-y-2 font-sans text-xs" data-id="element-1510">
                <li data-id="element-1511">
                  <button onClick={() => openPolicy('privacy')} className="hover:text-white transition-colors" data-id="element-1512">
                    Privacy Policy
                  </button>
                </li>
                <li data-id="element-1513">
                  <button onClick={() => openPolicy('terms')} className="hover:text-white transition-colors" data-id="element-1514">
                    Terms of Service
                  </button>
                </li>
                <li data-id="element-1515">
                  <button onClick={() => openPolicy('refund')} className="hover:text-white transition-colors" data-id="element-1516">
                    Refund Policy
                  </button>
                </li>
              </ul>
            </div>
            <div data-id="element-1517">
              <h5 className="text-white font-sans font-bold uppercase tracking-widest mb-4" data-id="element-1518">
                Disclaimer
              </h5>
              <p className="font-serif text-xs leading-relaxed" data-id="element-1519">
                These statements have not been evaluated by the Food and Drug
                Administration. This product is not intended to diagnose, treat,
                cure, or prevent any disease. Individual results may vary.
              </p>
            </div>
          </div>
          <div className="border-t border-stone-800 pt-8 text-center font-sans text-xs text-stone-600 uppercase tracking-widest" data-id="element-1520">
            © {new Date().getFullYear()} GINEXT. All Rights Reserved.
          </div>
        </div>
      </footer>

      <PolicyModal isOpen={policyModal.isOpen} onClose={closePolicy} type={policyModal.type} data-id="element-1521" />

      <FloatingCTA ctaText="Try GINEXT Risk-Free" ctaLink="/#bundles" subText="Natural warming relief • 30-Day Guarantee" data-id="element-1522" />
    </div>;
}